import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import BrandedConfirmModal from '../components/BrandedConfirmModal';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { nonCourseExerciseTypesUrl, exerciseTypesByGradeUrl } from '../utils/exerciseApi';
import {
  canCreateNonCourseExerciseOrType,
  filterCoursesForExerciseCreation,
  validateExerciseTypeCreationAccess,
} from '../utils/exerciseCreationAccess';
import { isGradeBeyondViewerAccess } from '../utils/gradeAccess';

function sortExercisesByOrder(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const oa = a.exercise_order ?? 0;
    const ob = b.exercise_order ?? 0;
    if (oa !== ob) return oa - ob;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

const DROPDOWN = {
  NEW_COURSE: 'newCourse',
  NEW_GRADE: 'newGrade',
  EDIT_COURSE: 'editCourse',
  EDIT_COURSE_TYPE: 'editCourseType',
  EDIT_GRADE: 'editGrade',
  EDIT_GRADE_TYPE: 'editGradeType',
  EDIT_NON_COURSE_TYPE: 'editNonCourseType',
};

function PickDropdown({
  open,
  onToggle,
  onClose,
  disabled,
  selectedLabel,
  placeholder,
  hasOptions,
  title,
  styles,
  children,
}) {
  return (
    <>
      <View style={styles.pickDropdownWrap}>
        <TouchableOpacity
          style={styles.pickTrigger}
          onPress={onToggle}
          disabled={disabled}
          activeOpacity={0.7}
          {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
        >
          <Text
            style={[styles.pickTriggerText, !selectedLabel && styles.pickTriggerPlaceholder]}
            numberOfLines={1}
          >
            {selectedLabel || placeholder}
          </Text>
          {hasOptions ? (
            <Text style={styles.pickTriggerArrow}>{open ? '▲' : '▼'}</Text>
          ) : null}
        </TouchableOpacity>
      </View>

      <Modal
        visible={Boolean(open && hasOptions)}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
        <View style={styles.pickerModalRoot}>
          <Pressable style={styles.pickerModalBackdrop} onPress={onClose} />
          <View
            style={styles.dropdownSheet}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
          >
            {title ? <Text style={styles.dropdownSheetTitle}>{title}</Text> : null}
            <ScrollView
              style={styles.dropdownScroll}
              contentContainerStyle={styles.dropdownScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {children}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
}

function webDataSet(attrs) {
  return Platform.OS === 'web' ? { dataSet: attrs } : {};
}

function MovePickDropdown({
  styles,
  triggerText,
  isPlaceholder,
  disabled,
  open,
  onToggle,
  hasArrow,
  children,
}) {
  return (
    <View style={styles.moveDestDropdownWrap} {...webDataSet({ exerciseTypeBuilderMoveDest: 'true' })}>
      <TouchableOpacity
        style={[styles.pickTrigger, disabled && styles.pickTriggerDisabled]}
        onPress={onToggle}
        disabled={disabled}
        {...webDataSet({ dropdownTrigger: 'true' })}
      >
        <Text
          style={[styles.pickTriggerText, isPlaceholder && styles.pickTriggerPlaceholder]}
          numberOfLines={2}
          {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
        >
          {triggerText}
        </Text>
        {hasArrow ? (
          <Text
            style={styles.pickTriggerArrow}
            {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
          >
            {open ? '▲' : '▼'}
          </Text>
        ) : null}
      </TouchableOpacity>
      {open ? (
        <View
          style={styles.moveTypeDropdownList}
          {...webDataSet({ dropdownSurface: 'true', exerciseTypeBuilderMoveDestList: 'true' })}
        >
          {children}
        </View>
      ) : null}
    </View>
  );
}

function MovePickOptions({
  styles,
  items,
  selectedId,
  currentId,
  currentSuffix,
  getId,
  getName,
  onSelect,
  emptyText,
}) {
  const resolveId = getId || ((item) => item.id);
  if (!items || items.length === 0) {
    return (
      <Text
        style={[styles.dropdownOptionText, styles.moveDestEmptyText]}
        {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
      >
        {emptyText}
      </Text>
    );
  }
  return (
    <ScrollView
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      style={styles.moveDestOptionsScroll}
    >
      {items.map((item) => {
        const itemId = resolveId(item);
        const isCurrent = currentId != null && itemId === currentId;
        const isSelected = !isCurrent && selectedId != null && itemId === selectedId;
        const name = getName(item);
        return (
          <TouchableOpacity
            key={String(itemId)}
            style={[
              styles.dropdownOption,
              isSelected && styles.dropdownOptionSelected,
              isCurrent && styles.dropdownOptionDisabled,
            ]}
            onPress={() => onSelect(item)}
            disabled={isCurrent}
            activeOpacity={isCurrent ? 1 : 0.7}
            {...webDataSet({
              dropdownRow: 'true',
              exerciseTypeBuilderMoveOptionDisabled: isCurrent ? 'true' : 'false',
            })}
          >
            <Text
              style={[
                styles.dropdownOptionText,
                isSelected && styles.dropdownOptionTextSelected,
                isCurrent && styles.dropdownOptionTextDisabled,
              ]}
              {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
            >
              {isCurrent ? `${name} (${currentSuffix})` : name}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}

export default function ExerciseTypeBuilder({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);

  const [builderTab, setBuilderTab] = useState('new');
  const [saving, setSaving] = useState(false);

  // New tab form state
  const [exerciseTypeName, setExerciseTypeName] = useState('');
  const [equipmentNeeded, setEquipmentNeeded] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  /** @type {'course' | 'grade' | 'nonCourse' | null} */
  const [newSelectionPath, setNewSelectionPath] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Edit tab state
  const [editCourseId, setEditCourseId] = useState(null);
  const [editExerciseTypes, setEditExerciseTypes] = useState([]);
  const [editNonCourseExerciseTypes, setEditNonCourseExerciseTypes] = useState([]);
  const [loadingNonCourseTypes, setLoadingNonCourseTypes] = useState(false);
  const [editGradeId, setEditGradeId] = useState(null);
  const [editGradeExerciseTypes, setEditGradeExerciseTypes] = useState([]);
  const [loadingGradeExerciseTypes, setLoadingGradeExerciseTypes] = useState(false);
  /** @type {'course' | 'nonCourse' | 'grade' | null} */
  const [editSelectionSource, setEditSelectionSource] = useState(null);
  const [editExerciseTypeId, setEditExerciseTypeId] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');
  const [originalTypeName, setOriginalTypeName] = useState('');
  const [editExerciseOrderList, setEditExerciseOrderList] = useState([]);
  const [loadingEditExercises, setLoadingEditExercises] = useState(false);
  const [pendingMoves, setPendingMoves] = useState([]);
  const [openMoveMenuExerciseId, setOpenMoveMenuExerciseId] = useState(null);
  const [editSaveStatus, setEditSaveStatus] = useState(null);
  const [editErrorMessage, setEditErrorMessage] = useState('');
  const [moveTypeMode, setMoveTypeMode] = useState(false);
  const [moveSelectedTypeIds, setMoveSelectedTypeIds] = useState(() => new Set());
  const [moveDestKind, setMoveDestKind] = useState(null);
  const [moveDestCourseId, setMoveDestCourseId] = useState(null);
  const [moveDestGradeId, setMoveDestGradeId] = useState(null);
  const [showMoveDestCourseDropdown, setShowMoveDestCourseDropdown] = useState(false);
  const [showMoveDestGradeDropdown, setShowMoveDestGradeDropdown] = useState(false);
  const [showMoveDestNonCourseDropdown, setShowMoveDestNonCourseDropdown] = useState(false);
  const [movingTypes, setMovingTypes] = useState(false);
  const [openTypeDeleteMenuId, setOpenTypeDeleteMenuId] = useState(null);
  const [typeDeleteTarget, setTypeDeleteTarget] = useState(null);
  const [typeDeleteTypedValue, setTypeDeleteTypedValue] = useState('');
  const [deletingType, setDeletingType] = useState(false);
  const [typeDeleteError, setTypeDeleteError] = useState('');
  const typeDeleteGenRef = useRef(0);

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetchUser(userId);
    fetchCourses();
    fetchGrades();
    const preCourse = route?.params?.preselectedCourseId;
    if (preCourse != null) {
      setSelectedCourseId(preCourse);
      setNewSelectionPath('course');
    }
  }, []);

  useEffect(() => {
    if ((builderTab === 'edit' || builderTab === 'new') && currentUser?.id) {
      void fetchNonCourseExerciseTypes();
    }
  }, [currentUser?.id, builderTab]);

  useEffect(() => {
    const preCourse = route?.params?.preselectedCourseId;
    if (preCourse != null) {
      setSelectedCourseId(preCourse);
      setNewSelectionPath('course');
    }
  }, [route?.params?.preselectedCourseId]);

  useFocusEffect(
    useCallback(() => {
      if (builderTab === 'edit' && editCourseId) {
        void fetchExerciseTypesForCourse(editCourseId, { preserveSelection: true });
      }
      if (builderTab === 'edit' && editGradeId) {
        void fetchExerciseTypesForGrade(editGradeId, { preserveSelection: true });
      }
      if ((builderTab === 'edit' || builderTab === 'new') && currentUser?.id) {
        void fetchNonCourseExerciseTypes({ preserveSelection: true });
      }
    }, [builderTab, editCourseId, editGradeId, editExerciseTypeId, currentUser?.id, editSelectionSource])
  );

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        setGrades(data.sort((a, b) => a.grade_rank_order - b.grade_rank_order));
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchNonCourseExerciseTypes = async ({ preserveSelection = false } = {}) => {
    const viewerId = currentUser?.id;
    if (viewerId == null) {
      setEditNonCourseExerciseTypes([]);
      return;
    }
    setLoadingNonCourseTypes(true);
    try {
      const response = await fetch(nonCourseExerciseTypesUrl({ viewerId }));
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setEditNonCourseExerciseTypes(rows);
        if (
          preserveSelection &&
          editSelectionSource === 'nonCourse' &&
          editExerciseTypeId != null
        ) {
          const stillExists = rows.some((t) => t.id === editExerciseTypeId);
          if (!stillExists) {
            handleEditExerciseTypeClear();
          }
        }
      } else {
        setEditNonCourseExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching non-course exercise types:', error);
      setEditNonCourseExerciseTypes([]);
    } finally {
      setLoadingNonCourseTypes(false);
    }
  };

  const fetchExerciseTypesForGrade = async (gradeId, { preserveSelection = false } = {}) => {
    if (gradeId == null) {
      setEditGradeExerciseTypes([]);
      if (!preserveSelection) {
        handleEditExerciseTypeClear();
      }
      return;
    }
    setLoadingGradeExerciseTypes(true);
    try {
      const response = await fetch(exerciseTypesByGradeUrl(gradeId));
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setEditGradeExerciseTypes(rows);
        if (preserveSelection && editSelectionSource === 'grade' && editExerciseTypeId != null) {
          const stillExists = rows.some((t) => t.id === editExerciseTypeId);
          if (!stillExists) {
            handleEditExerciseTypeClear();
          }
        }
      } else {
        setEditGradeExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching grade exercise types:', error);
      setEditGradeExerciseTypes([]);
    } finally {
      setLoadingGradeExerciseTypes(false);
    }
  };

  const fetchExerciseTypesForCourse = async (courseId, { preserveSelection = false } = {}) => {
    if (courseId == null) {
      setEditExerciseTypes([]);
      if (!preserveSelection) {
        setEditExerciseTypeId(null);
      }
      return;
    }
    try {
      const response = await fetch(
        `${getApiUrl()}/api/exercise-types/course/${encodeURIComponent(String(courseId))}`
      );
      if (response.ok) {
        const data = await response.json();
        setEditExerciseTypes(Array.isArray(data) ? data : []);
        if (preserveSelection && editSelectionSource === 'course' && editExerciseTypeId != null) {
          const stillExists = data.some((t) => t.id === editExerciseTypeId);
          if (!stillExists) {
            handleEditExerciseTypeClear();
          }
        }
      } else {
        setEditExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching exercise types:', error);
      setEditExerciseTypes([]);
    }
  };

  const loadExercisesForEditType = async (exerciseTypeId) => {
    if (exerciseTypeId == null) {
      setEditExerciseOrderList([]);
      return;
    }
    setLoadingEditExercises(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(exerciseTypeId))}`
      );
      const data = res.ok ? await res.json() : [];
      setEditExerciseOrderList(
        sortExercisesByOrder(data).map((e) => ({
          id: e.id,
          exercise_name: e.exercise_name,
        }))
      );
    } catch (error) {
      console.error('Error loading exercises:', error);
      setEditExerciseOrderList([]);
    } finally {
      setLoadingEditExercises(false);
    }
  };

  const closeAllDropdowns = () => setOpenDropdown(null);

  const toggleDropdown = (key) => {
    setOpenDropdown((current) => (current === key ? null : key));
  };

  const resetNewForm = () => {
    setExerciseTypeName('');
    setEquipmentNeeded('');
    setSelectedCourseId(null);
    setSelectedGradeId(null);
    setNewSelectionPath(null);
    closeAllDropdowns();
    setSaveStatus(null);
    setErrorMessage('');
  };

  const handleEditExerciseTypeClear = () => {
    setEditExerciseTypeId(null);
    setEditSelectionSource(null);
    setEditTypeName('');
    setOriginalTypeName('');
    setEditExerciseOrderList([]);
    setPendingMoves([]);
    setOpenMoveMenuExerciseId(null);
    setEditSaveStatus(null);
    setEditErrorMessage('');
  };

  const closeAllMoveDestDropdowns = () => {
    setShowMoveDestCourseDropdown(false);
    setShowMoveDestGradeDropdown(false);
    setShowMoveDestNonCourseDropdown(false);
  };

  const resetMoveTypeDestination = () => {
    setMoveDestKind(null);
    setMoveDestCourseId(null);
    setMoveDestGradeId(null);
    closeAllMoveDestDropdowns();
  };

  const resetMoveTypeState = () => {
    setMoveTypeMode(false);
    setMoveSelectedTypeIds(new Set());
    setMovingTypes(false);
    resetMoveTypeDestination();
  };

  const clearEditTypeFormKeepParent = () => {
    setEditExerciseTypeId(null);
    setEditTypeName('');
    setOriginalTypeName('');
    setEditExerciseOrderList([]);
    setPendingMoves([]);
    setOpenMoveMenuExerciseId(null);
  };

  const handleMoveTypeModeToggle = (enabled) => {
    setMoveTypeMode(enabled);
    setMoveSelectedTypeIds(new Set());
    resetMoveTypeDestination();
    setOpenTypeDeleteMenuId(null);
    if (enabled) {
      clearEditTypeFormKeepParent();
    }
  };

  const fetchExerciseCountForType = async (typeId) => {
    try {
      const res = await fetch(
        `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(typeId))}`
      );
      const data = res.ok ? await res.json() : [];
      return Array.isArray(data) ? data.length : 0;
    } catch {
      return 0;
    }
  };

  const openTypeDeleteConfirm = async (exerciseType) => {
    setOpenTypeDeleteMenuId(null);
    setTypeDeleteError('');
    setTypeDeleteTypedValue('');
    let exerciseCount = 0;
    if (editExerciseTypeId === exerciseType.id) {
      exerciseCount = editExerciseOrderList.length + pendingMoves.length;
    } else {
      exerciseCount = await fetchExerciseCountForType(exerciseType.id);
    }
    setTypeDeleteTarget({
      id: exerciseType.id,
      name: exerciseType.exercise_type_name || 'this exercise type',
      exerciseCount,
    });
  };

  const dismissTypeDeleteConfirm = () => {
    if (deletingType) return;
    typeDeleteGenRef.current += 1;
    setTypeDeleteTarget(null);
    setTypeDeleteTypedValue('');
    setTypeDeleteError('');
  };

  const handleDeleteTypeConfirm = async () => {
    if (!typeDeleteTarget?.id) return;
    if (String(typeDeleteTypedValue).trim().toLowerCase() !== 'delete') return;
    const gen = ++typeDeleteGenRef.current;
    setDeletingType(true);
    setTypeDeleteError('');
    try {
      const res = await fetch(
        `${getApiUrl()}/api/exercise-types/${encodeURIComponent(String(typeDeleteTarget.id))}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete exercise type.');
      }
      const payload = await res.json().catch(() => ({}));
      if (gen !== typeDeleteGenRef.current) return;
      const deletedId = typeDeleteTarget.id;
      const deletedCount =
        typeof payload.deleted_exercise_count === 'number'
          ? payload.deleted_exercise_count
          : typeDeleteTarget.exerciseCount;
      setTypeDeleteTarget(null);
      setTypeDeleteTypedValue('');
      if (editExerciseTypeId === deletedId) {
        clearEditTypeFormKeepParent();
      }
      setEditSaveStatus('success');
      setEditErrorMessage(
        deletedCount > 0
          ? `Exercise type deleted. ${deletedCount} exercise${deletedCount === 1 ? '' : 's'} removed.`
          : 'Exercise type deleted successfully.'
      );
      if (editCourseId) void fetchExerciseTypesForCourse(editCourseId);
      if (editGradeId) void fetchExerciseTypesForGrade(editGradeId);
      if (!editCourseId && !editGradeId && currentUser?.id) {
        void fetchNonCourseExerciseTypes();
      }
    } catch (error) {
      if (gen !== typeDeleteGenRef.current) return;
      console.error('Error deleting exercise type:', error);
      setTypeDeleteError(error.message || 'Failed to delete exercise type.');
    } finally {
      if (gen === typeDeleteGenRef.current) {
        setDeletingType(false);
      }
    }
  };

  const handleMoveTypeListPress = (exerciseType, source) => {
    setOpenTypeDeleteMenuId(null);
    if (!moveTypeMode) {
      if (source === 'course') handleEditExerciseTypeSelect(exerciseType);
      else if (source === 'grade') handleEditGradeExerciseTypeSelect(exerciseType);
      else handleEditNonCourseExerciseTypeSelect(exerciseType);
      return;
    }
    setMoveSelectedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(exerciseType.id)) next.delete(exerciseType.id);
      else next.add(exerciseType.id);
      return next;
    });
    resetMoveTypeDestination();
  };

  const toggleMoveDestDropdown = (key) => {
    const currentlyOpen = {
      course: showMoveDestCourseDropdown,
      grade: showMoveDestGradeDropdown,
      nonCourse: showMoveDestNonCourseDropdown,
    }[key];
    closeAllMoveDestDropdowns();
    if (currentlyOpen) return;
    if (key === 'course') setShowMoveDestCourseDropdown(true);
    if (key === 'grade') setShowMoveDestGradeDropdown(true);
    if (key === 'nonCourse') setShowMoveDestNonCourseDropdown(true);
  };

  const handleMoveDestCourseSelect = (course) => {
    if (editCourseId === course.id) return;
    setMoveDestKind('course');
    setMoveDestCourseId(course.id);
    setMoveDestGradeId(null);
    closeAllMoveDestDropdowns();
  };

  const handleMoveDestGradeSelect = (grade) => {
    if (editGradeId === grade.id) return;
    setMoveDestKind('grade');
    setMoveDestGradeId(grade.id);
    setMoveDestCourseId(null);
    closeAllMoveDestDropdowns();
  };

  const handleMoveDestNonCourseSelect = () => {
    if (!editCourseId && !editGradeId) return;
    setMoveDestKind('nonCourse');
    setMoveDestCourseId(null);
    setMoveDestGradeId(null);
    closeAllMoveDestDropdowns();
  };

  const handleMoveTypesCancel = () => {
    resetMoveTypeState();
  };

  const handleMoveTypesUpdate = async () => {
    if (moveSelectedTypeIds.size === 0 || moveDestKind == null) return;
    if (moveDestKind === 'course' && (moveDestCourseId == null || moveDestCourseId === editCourseId)) {
      return;
    }
    if (moveDestKind === 'grade' && (moveDestGradeId == null || moveDestGradeId === editGradeId)) {
      return;
    }
    if (moveDestKind === 'nonCourse' && !editCourseId && !editGradeId) {
      return;
    }
    setMovingTypes(true);
    try {
      const ids = [...moveSelectedTypeIds];
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`${getApiUrl()}/api/exercise-types/${encodeURIComponent(String(id))}/move`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              destination: moveDestKind,
              course_id: moveDestKind === 'course' ? moveDestCourseId : null,
              grade_id: moveDestKind === 'grade' ? moveDestGradeId : null,
              student_id: currentUser?.id ?? null,
            }),
          })
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        const errBody = await failed[0].json().catch(() => ({}));
        throw new Error(
          errBody.error || `Failed to move ${failed.length} exercise type(s). Please try again.`
        );
      }
      setEditSaveStatus('success');
      setEditErrorMessage(
        ids.length === 1
          ? 'Exercise type moved successfully.'
          : `${ids.length} exercise types moved successfully.`
      );
      resetMoveTypeState();
      if (editCourseId) void fetchExerciseTypesForCourse(editCourseId, { preserveSelection: true });
      if (editGradeId) void fetchExerciseTypesForGrade(editGradeId, { preserveSelection: true });
      if (currentUser?.id) void fetchNonCourseExerciseTypes({ preserveSelection: true });
    } catch (error) {
      console.error('Error moving exercise types:', error);
      setEditSaveStatus('error');
      setEditErrorMessage(error.message || 'Failed to move exercise type(s).');
    } finally {
      setMovingTypes(false);
    }
  };

  const clearEditGradeSelection = () => {
    setEditGradeId(null);
    setEditGradeExerciseTypes([]);
  };

  const handleEditCancel = () => {
    setEditCourseId(null);
    setEditExerciseTypes([]);
    closeAllDropdowns();
    clearEditGradeSelection();
    handleEditExerciseTypeClear();
    resetMoveTypeState();
    setOpenTypeDeleteMenuId(null);
    if (!deletingType) {
      setTypeDeleteTarget(null);
      setTypeDeleteTypedValue('');
      setTypeDeleteError('');
    }
    setLoadingEditExercises(false);
  };

  const switchBuilderTab = (tab) => {
    setBuilderTab(tab);
    setSaveStatus(null);
    setErrorMessage('');
    setEditSaveStatus(null);
    setEditErrorMessage('');
    setOpenMoveMenuExerciseId(null);
    setOpenTypeDeleteMenuId(null);
    closeAllDropdowns();
    if (tab === 'edit') {
      resetNewForm();
    } else {
      handleEditCancel();
      const preCourse = route?.params?.preselectedCourseId;
      if (preCourse != null) {
        setSelectedCourseId(preCourse);
        setNewSelectionPath('course');
      }
    }
  };

  const handleNewReset = () => {
    resetNewForm();
  };

  const handleEditReset = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    handleEditCancel();
    void fetchCourses();
  };

  const handleEditCourseSelect = (course) => {
    if (editCourseId === course.id) {
      handleEditCancel();
      return;
    }
    clearEditGradeSelection();
    setEditCourseId(course.id);
    handleEditExerciseTypeClear();
    resetMoveTypeState();
    closeAllDropdowns();
    void fetchExerciseTypesForCourse(course.id);
  };

  const handleEditGradeSelect = (grade) => {
    if (editGradeId === grade.id) {
      clearEditGradeSelection();
      handleEditExerciseTypeClear();
      resetMoveTypeState();
      closeAllDropdowns();
      return;
    }
    setEditCourseId(null);
    setEditExerciseTypes([]);
    setEditGradeId(grade.id);
    handleEditExerciseTypeClear();
    resetMoveTypeState();
    closeAllDropdowns();
    void fetchExerciseTypesForGrade(grade.id);
  };

  const handleEditGradeExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'grade' && editExerciseTypeId === exerciseType.id) {
      handleEditExerciseTypeClear();
      closeAllDropdowns();
      return;
    }
    setEditSelectionSource('grade');
    setEditExerciseTypeId(exerciseType.id);
    setEditTypeName(exerciseType.exercise_type_name || '');
    setOriginalTypeName(exerciseType.exercise_type_name || '');
    setPendingMoves([]);
    setOpenMoveMenuExerciseId(null);
    setEditSaveStatus(null);
    setEditErrorMessage('');
    closeAllDropdowns();
    void loadExercisesForEditType(exerciseType.id);
  };

  const handleEditExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'course' && editExerciseTypeId === exerciseType.id) {
      handleEditExerciseTypeClear();
      closeAllDropdowns();
      return;
    }
    setEditSelectionSource('course');
    setEditExerciseTypeId(exerciseType.id);
    setEditTypeName(exerciseType.exercise_type_name || '');
    setOriginalTypeName(exerciseType.exercise_type_name || '');
    setPendingMoves([]);
    setOpenMoveMenuExerciseId(null);
    setEditSaveStatus(null);
    setEditErrorMessage('');
    closeAllDropdowns();
    void loadExercisesForEditType(exerciseType.id);
  };

  const handleNewCourseSelect = (course) => {
    if (selectedCourseId === course.id && newSelectionPath === 'course') {
      setSelectedCourseId(null);
      setNewSelectionPath(null);
      closeAllDropdowns();
      return;
    }
    setSelectedGradeId(null);
    setSelectedCourseId(course.id);
    setNewSelectionPath('course');
    closeAllDropdowns();
  };

  const handleNewGradeSelect = (grade) => {
    if (isGradeBeyondViewerAccess(grade, currentUser)) return;
    if (selectedGradeId === grade.id && newSelectionPath === 'grade') {
      setSelectedGradeId(null);
      setNewSelectionPath(null);
      closeAllDropdowns();
      return;
    }
    setSelectedCourseId(null);
    setSelectedGradeId(grade.id);
    setNewSelectionPath('grade');
    closeAllDropdowns();
  };

  const handleNewNonCoursePathSelect = () => {
    if (newSelectionPath === 'nonCourse') {
      setNewSelectionPath(null);
      closeAllDropdowns();
      return;
    }
    setSelectedCourseId(null);
    setSelectedGradeId(null);
    setNewSelectionPath('nonCourse');
    closeAllDropdowns();
  };

  const handleEditNonCourseExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'nonCourse' && editExerciseTypeId === exerciseType.id) {
      handleEditExerciseTypeClear();
      closeAllDropdowns();
      return;
    }
    setEditCourseId(null);
    setEditExerciseTypes([]);
    clearEditGradeSelection();
    setEditSelectionSource('nonCourse');
    setEditExerciseTypeId(exerciseType.id);
    setEditTypeName(exerciseType.exercise_type_name || '');
    setOriginalTypeName(exerciseType.exercise_type_name || '');
    setPendingMoves([]);
    setOpenMoveMenuExerciseId(null);
    setEditSaveStatus(null);
    setEditErrorMessage('');
    closeAllDropdowns();
    void loadExercisesForEditType(exerciseType.id);
  };

  const adoptionTypeDeepLinkRef = useRef(false);

  useEffect(() => {
    const typeId = route?.params?.openEditExerciseTypeId;
    const ctx = route?.params?.adoptionEditContext;
    if (!typeId || adoptionTypeDeepLinkRef.current || !currentUser?.id) return;

    adoptionTypeDeepLinkRef.current = true;
    void (async () => {
      try {
        setBuilderTab('edit');
        closeAllDropdowns();
        const source = ctx?.selection_source || 'nonCourse';

        if (source === 'course' && ctx?.course_id != null) {
          clearEditGradeSelection();
          setEditCourseId(Number(ctx.course_id));
          await fetchExerciseTypesForCourse(Number(ctx.course_id));
          setEditSelectionSource('course');
        } else if (source === 'grade' && ctx?.grade_id != null) {
          setEditCourseId(null);
          setEditExerciseTypes([]);
          setEditGradeId(Number(ctx.grade_id));
          await fetchExerciseTypesForGrade(Number(ctx.grade_id));
          setEditSelectionSource('grade');
        } else {
          setEditCourseId(null);
          setEditExerciseTypes([]);
          clearEditGradeSelection();
          await fetchNonCourseExerciseTypes();
          setEditSelectionSource('nonCourse');
        }

        const typeRes = await fetch(
          `${getApiUrl()}/api/exercise-types/${encodeURIComponent(String(typeId))}`
        );
        if (!typeRes.ok) throw new Error('Exercise type not found');
        const et = await typeRes.json();
        setEditExerciseTypeId(Number(typeId));
        setEditTypeName(et.exercise_type_name || '');
        setOriginalTypeName(et.exercise_type_name || '');
        setPendingMoves([]);
        setOpenMoveMenuExerciseId(null);
        setEditSaveStatus(null);
        setEditErrorMessage('');
        await loadExercisesForEditType(Number(typeId));
      } catch (e) {
        console.error('Adoption deep link exercise type:', e);
        adoptionTypeDeepLinkRef.current = false;
      }
    })();
  }, [route?.params?.openEditExerciseTypeId, route?.params?.adoptionEditContext, currentUser?.id]);

  const creatableCourses = useMemo(
    () => filterCoursesForExerciseCreation(currentUser, courses),
    [currentUser, courses]
  );

  const canCreateNonCourse = canCreateNonCourseExerciseOrType(currentUser);

  const activeNewPath =
    newSelectionPath ??
    (selectedCourseId != null ? 'course' : selectedGradeId != null ? 'grade' : null);

  const isNewCourseSectionLocked = activeNewPath != null && activeNewPath !== 'course';
  const isNewGradeSectionLocked = activeNewPath != null && activeNewPath !== 'grade';
  const isNewNonCourseSectionLocked =
    !canCreateNonCourse || (activeNewPath != null && activeNewPath !== 'nonCourse');

  useEffect(() => {
    if (isNewCourseSectionLocked) {
      closeAllDropdowns();
    }
  }, [isNewCourseSectionLocked]);

  useEffect(() => {
    if (isNewGradeSectionLocked) {
      closeAllDropdowns();
    }
  }, [isNewGradeSectionLocked]);

  const selectedGradeLabel =
    editGradeId != null
      ? grades.find((g) => g.id === editGradeId)?.grade_name || 'Selected grade'
      : null;

  const selectedNewCourseLabel =
    selectedCourseId != null
      ? courses.find((c) => c.id === selectedCourseId)?.course_name || 'Selected course'
      : null;

  const selectedNewGradeLabel =
    selectedGradeId != null
      ? grades.find((g) => g.id === selectedGradeId)?.grade_name || 'Selected grade'
      : null;

  const selectedEditCourseLabel =
    editCourseId != null
      ? courses.find((c) => c.id === editCourseId)?.course_name || 'Selected course'
      : null;

  const selectedEditCourseExerciseTypeLabel =
    editSelectionSource === 'course' && editExerciseTypeId != null
      ? editExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name ||
        editTypeName ||
        'Selected exercise type'
      : null;

  const selectedGradeExerciseTypeLabel =
    editSelectionSource === 'grade' && editExerciseTypeId != null
      ? editGradeExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name ||
        editTypeName ||
        'Selected exercise type'
      : null;

  const selectedNonCourseTypeLabel =
    editSelectionSource === 'nonCourse' && editExerciseTypeId != null
      ? editNonCourseExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name ||
        editTypeName ||
        'Selected exercise type'
      : null;

  const currentMoveCourseId = editCourseId;
  const currentMoveGradeId = editGradeId;
  const isCurrentlyNonCourseParent = editCourseId == null && editGradeId == null;
  const selectedMoveDestCourseName =
    moveDestKind === 'course' && moveDestCourseId != null
      ? courses.find((c) => c.id === moveDestCourseId)?.course_name
      : null;
  const selectedMoveDestGradeName =
    moveDestKind === 'grade' && moveDestGradeId != null
      ? grades.find((g) => g.id === moveDestGradeId)?.grade_name
      : null;
  const canSubmitMoveTypes =
    moveSelectedTypeIds.size > 0 &&
    !movingTypes &&
    ((moveDestKind === 'course' &&
      moveDestCourseId != null &&
      moveDestCourseId !== currentMoveCourseId) ||
      (moveDestKind === 'grade' &&
        moveDestGradeId != null &&
        moveDestGradeId !== currentMoveGradeId) ||
      (moveDestKind === 'nonCourse' && !isCurrentlyNonCourseParent));

  const handleMoveExerciseOrder = (index, direction) => {
    setOpenMoveMenuExerciseId(null);
    setEditExerciseOrderList((prev) => {
      const next = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  };

  const otherExerciseTypesForMove =
    editSelectionSource === 'nonCourse'
      ? editNonCourseExerciseTypes.filter((t) => t.id !== editExerciseTypeId)
      : editSelectionSource === 'grade'
        ? editGradeExerciseTypes.filter((t) => t.id !== editExerciseTypeId)
        : editExerciseTypes.filter((t) => t.id !== editExerciseTypeId);

  const moveTargetScopeLabel =
    editSelectionSource === 'nonCourse'
      ? 'non-course/grade type'
      : editSelectionSource === 'grade'
        ? 'type in this grade'
        : 'type in this course';

  const handleMoveExerciseToType = (exercise, targetType) => {
    setOpenMoveMenuExerciseId(null);
    setEditExerciseOrderList((prev) => prev.filter((item) => item.id !== exercise.id));
    setPendingMoves((prev) => [
      ...prev.filter((m) => m.exerciseId !== exercise.id),
      {
        exerciseId: exercise.id,
        exerciseName: exercise.exercise_name,
        targetTypeId: targetType.id,
        targetTypeName: targetType.exercise_type_name,
      },
    ]);
  };

  const handleCreateExerciseTypeFromEdit = () => {
    setOpenMoveMenuExerciseId(null);
    const courseId = editCourseId;
    const gradeId = editGradeId;
    switchBuilderTab('new');
    if (courseId != null) {
      setSelectedCourseId(courseId);
    }
    if (gradeId != null) {
      setSelectedGradeId(gradeId);
    }
  };

  const persistExerciseOrderOnly = async (exerciseId, orderValue) => {
    const putRes = await fetch(
      `${getApiUrl()}/api/exercises/${encodeURIComponent(String(exerciseId))}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exercise_name: null,
          sets: null,
          reps: null,
          exercise_type_id: null,
          description: null,
          display_url: null,
          exercise_order: orderValue,
          display_images: null,
        }),
      }
    );
    const putRaw = await putRes.text();
    let putBody = {};
    try {
      putBody = putRaw ? JSON.parse(putRaw) : {};
    } catch {
      /* ignore */
    }
    if (!putRes.ok) {
      throw new Error(putBody.error || 'Failed to save exercise order');
    }
  };

  const getNextOrderForTargetType = async (targetTypeId, extraMovesToSameType = 0) => {
    const res = await fetch(
      `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(targetTypeId))}`
    );
    const rows = res.ok ? await res.json() : [];
    const maxOrder = sortExercisesByOrder(rows).reduce(
      (max, row) => Math.max(max, row.exercise_order ?? 0),
      0
    );
    return maxOrder + 1 + extraMovesToSameType;
  };

  const handleNewSave = async () => {
    if (!exerciseTypeName.trim()) {
      setSaveStatus('error');
      setErrorMessage('Exercise Type Name is required');
      return;
    }

    const creationAccessError = validateExerciseTypeCreationAccess(currentUser, {
      selectedCourseId,
      selectedGradeId,
      courses,
      grades,
    });
    if (creationAccessError) {
      setSaveStatus('error');
      setErrorMessage(creationAccessError);
      return;
    }

    setSaving(true);
    try {
      const exerciseTypeResponse = await fetch(`${getApiUrl()}/api/exercise-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exercise_type_name: exerciseTypeName.trim(),
          equipment_needed: equipmentNeeded.trim() || null,
          created_by: currentUser?.id ?? null,
          course_id: selectedCourseId || null,
          grade_id: selectedGradeId || null,
        }),
      });

      if (!exerciseTypeResponse.ok) {
        const errorData = await exerciseTypeResponse.json().catch(() => ({ error: 'Unknown error' }));
        setSaveStatus('error');
        setErrorMessage(errorData.error || 'Failed to create exercise type');
        return;
      }

      const exerciseTypeData = await exerciseTypeResponse.json();
      const newExerciseTypeId = exerciseTypeData.id;

      if (selectedCourseId) {
        const linkRes = await fetch(`${getApiUrl()}/api/exercise-type-course`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            course_id: selectedCourseId,
            exercise_type_id: newExerciseTypeId,
            student_id: currentUser?.id ?? null,
          }),
        });
        if (!linkRes.ok) {
          const errorData = await linkRes.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to link exercise type to course');
        }
      }

      if (selectedGradeId) {
        const linkRes = await fetch(`${getApiUrl()}/api/exercise-type-grade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grade_id: selectedGradeId,
            exercise_type_id: newExerciseTypeId,
            student_id: currentUser?.id ?? null,
          }),
        });
        if (!linkRes.ok) {
          const errorData = await linkRes.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to link exercise type to grade');
        }
      }

      setSaveStatus('success');
      setErrorMessage('');

      const returnTo = route?.params?.returnTo;
      if (returnTo === 'ExerciseBuilder') {
        navigation.navigate('ExerciseBuilder', {
          userId: currentUser?.id || 1,
        });
      }
    } catch (error) {
      console.error('Error creating exercise type:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to create exercise type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (editExerciseTypeId == null) {
      setEditSaveStatus('error');
      setEditErrorMessage('Select an exercise type to edit.');
      return;
    }
    if (!editTypeName.trim()) {
      setEditSaveStatus('error');
      setEditErrorMessage('Exercise Type Name is required');
      return;
    }

    setSaving(true);
    setEditSaveStatus(null);
    setEditErrorMessage('');
    try {
      if (editTypeName.trim() !== originalTypeName.trim()) {
        const renameRes = await fetch(
          `${getApiUrl()}/api/exercise-types/${encodeURIComponent(String(editExerciseTypeId))}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              exercise_type_name: editTypeName.trim(),
              equipment_needed: null,
            }),
          }
        );
        if (!renameRes.ok) {
          const err = await renameRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to rename exercise type');
        }
      }

      for (let i = 0; i < editExerciseOrderList.length; i += 1) {
        const item = editExerciseOrderList[i];
        await persistExerciseOrderOnly(item.id, i + 1);
      }

      const movesByTarget = {};
      for (const move of pendingMoves) {
        if (!movesByTarget[move.targetTypeId]) {
          movesByTarget[move.targetTypeId] = [];
        }
        movesByTarget[move.targetTypeId].push(move);
      }

      for (const [targetTypeId, moves] of Object.entries(movesByTarget)) {
        let offset = 0;
        for (const move of moves) {
          const nextOrder = await getNextOrderForTargetType(Number(targetTypeId), offset);
          const moveRes = await fetch(
            `${getApiUrl()}/api/exercises/${encodeURIComponent(String(move.exerciseId))}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                exercise_name: null,
                sets: null,
                reps: null,
                exercise_type_id: Number(targetTypeId),
                description: null,
                display_url: null,
                exercise_order: nextOrder,
                display_images: null,
              }),
            }
          );
          if (!moveRes.ok) {
            const err = await moveRes.json().catch(() => ({}));
            throw new Error(
              err.error ||
                `Failed to move "${move.exerciseName}" to ${move.targetTypeName}`
            );
          }
          offset += 1;
        }
      }

      setEditSaveStatus('success');
      setEditErrorMessage('');
      setOriginalTypeName(editTypeName.trim());
      setPendingMoves([]);
      if (editSelectionSource === 'nonCourse') {
        void fetchNonCourseExerciseTypes({ preserveSelection: true });
      } else if (editSelectionSource === 'grade' && editGradeId) {
        void fetchExerciseTypesForGrade(editGradeId, { preserveSelection: true });
      } else if (editCourseId) {
        void fetchExerciseTypesForCourse(editCourseId, { preserveSelection: true });
      }
      void loadExercisesForEditType(editExerciseTypeId);
    } catch (error) {
      console.error('Error saving exercise type edits:', error);
      setEditSaveStatus('error');
      setEditErrorMessage(error.message || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderActionButtons = ({ onReset, onSave, resetLabel = 'Reset' }) => (
    <View style={styles.buttonContainer}>
      <View
        style={styles.resetBtnShell}
        {...(Platform.OS === 'web' ? { dataSet: { exerciseTypeBuilderResetBtn: 'true' } } : {})}
      >
        <TouchableOpacity
          style={styles.resetBtnInner}
          onPress={onReset}
          disabled={saving}
          activeOpacity={0.85}
        >
          <Text style={styles.resetBtnText}>{resetLabel}</Text>
        </TouchableOpacity>
      </View>
      <View
        style={styles.saveBtnShell}
        {...(Platform.OS === 'web' ? { dataSet: { exerciseTypeBuilderSaveBtn: 'true' } } : {})}
      >
        <TouchableOpacity
          style={styles.saveBtnInner}
          onPress={() => void onSave()}
          disabled={saving}
          activeOpacity={0.85}
        >
          {saving ? (
            <ActivityIndicator color="#000000" size="small" />
          ) : (
            <Text style={styles.saveBtnText}>Save</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  const styles = getStyles(theme);

  const renderMoveTypeToggle = () => (
    <View
      style={styles.moveTypeToggleWrap}
      {...webDataSet({ exerciseTypeBuilderMoveToggle: 'true' })}
    >
      <Text style={styles.moveTypeToggleLabel}>Move Exercise Type</Text>
      <View
        style={styles.segmentedToggle}
        accessibilityRole="tablist"
        accessibilityLabel="Move Exercise Type mode"
        {...webDataSet({ exerciseTypeBuilderMoveToggleTrack: 'true' })}
      >
        <TouchableOpacity
          style={[
            styles.segmentedToggleOption,
            !moveTypeMode && styles.segmentedToggleOptionActiveOff,
          ]}
          onPress={() => handleMoveTypeModeToggle(false)}
          disabled={saving || movingTypes}
          accessibilityRole="tab"
          accessibilityState={{ selected: !moveTypeMode }}
          accessibilityLabel="Move Exercise Type off"
          activeOpacity={0.85}
          {...webDataSet({
            exerciseTypeBuilderMoveToggleOption: 'off',
            exerciseTypeBuilderMoveToggleActive: !moveTypeMode ? 'true' : 'false',
          })}
        >
          <Text
            style={[
              styles.segmentedToggleText,
              !moveTypeMode && styles.segmentedToggleTextActive,
            ]}
          >
            OFF
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.segmentedToggleOption,
            moveTypeMode && styles.segmentedToggleOptionActiveOn,
          ]}
          onPress={() => handleMoveTypeModeToggle(true)}
          disabled={saving || movingTypes}
          accessibilityRole="tab"
          accessibilityState={{ selected: moveTypeMode }}
          accessibilityLabel="Move Exercise Type on"
          activeOpacity={0.85}
          {...webDataSet({
            exerciseTypeBuilderMoveToggleOption: 'on',
            exerciseTypeBuilderMoveToggleActive: moveTypeMode ? 'true' : 'false',
          })}
        >
          <Text
            style={[
              styles.segmentedToggleText,
              moveTypeMode && styles.segmentedToggleTextActive,
            ]}
          >
            ON
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTypePickList = (types, source, emptyText) => (
    <>
      {types.length === 0 ? (
        <Text style={styles.helpText}>{emptyText}</Text>
      ) : (
        <View
          style={styles.typePickList}
          {...webDataSet({ exerciseTypeBuilderPickList: 'true' })}
        >
          {types.map((exerciseType) => {
            const isMoveSelected = moveTypeMode && moveSelectedTypeIds.has(exerciseType.id);
            const isEditSelected =
              !moveTypeMode &&
              editSelectionSource === source &&
              editExerciseTypeId === exerciseType.id;
            const deleteMenuOpen =
              !moveTypeMode && openTypeDeleteMenuId === `${source}-${exerciseType.id}`;
            return (
              <View
                key={`${source}-${exerciseType.id}`}
                style={[
                  styles.typePickCard,
                  (isMoveSelected || isEditSelected) && styles.typePickCardSelected,
                  deleteMenuOpen && styles.typePickCardMenuOpen,
                ]}
                {...webDataSet({
                  exerciseTypeBuilderPickCard: 'true',
                  exerciseTypeBuilderPickCardSelected: isMoveSelected ? 'true' : 'false',
                })}
              >
                {isMoveSelected ? <View style={styles.typePickYellowOverlay} /> : null}
                <View style={styles.typePickCardInner}>
                  <TouchableOpacity
                    style={styles.typePickCardMain}
                    onPress={() => handleMoveTypeListPress(exerciseType, source)}
                    disabled={saving || movingTypes || deletingType}
                    activeOpacity={0.75}
                  >
                    <View style={styles.typePickCardContent}>
                      <View style={styles.typePickNameRow}>
                        {!moveTypeMode ? (
                          <MaterialIcons
                            name="edit"
                            size={20}
                            color={theme.colors.heading || theme.colors.primary || '#d4af37'}
                            style={styles.typePickEditIcon}
                          />
                        ) : null}
                        <Text
                          style={[
                            styles.typePickName,
                            isMoveSelected && styles.typePickNameSelected,
                          ]}
                        >
                          {exerciseType.exercise_type_name}
                        </Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                  {!moveTypeMode ? (
                    <View style={styles.cardMenuWrap}>
                      <TouchableOpacity
                        style={styles.cardMenuButton}
                        onPress={() =>
                          setOpenTypeDeleteMenuId((current) =>
                            current === `${source}-${exerciseType.id}`
                              ? null
                              : `${source}-${exerciseType.id}`
                          )
                        }
                        disabled={saving || movingTypes || deletingType}
                        accessibilityRole="button"
                        accessibilityLabel={`Actions for ${exerciseType.exercise_type_name}`}
                        {...webDataSet({ exerciseTypeBuilderCardMenuBtn: 'true' })}
                      >
                        <MaterialIcons name="more-vert" size={22} color={theme.colors.text} />
                      </TouchableOpacity>
                      {deleteMenuOpen ? (
                        <View
                          style={styles.cardDeleteMenu}
                          {...webDataSet({ exerciseTypeBuilderCardMenu: 'true' })}
                        >
                          <TouchableOpacity
                            style={styles.cardDeleteMenuItem}
                            onPress={() => void openTypeDeleteConfirm(exerciseType)}
                            {...webDataSet({ exerciseTypeBuilderCardMenuItem: 'true' })}
                          >
                            <Text
                              style={styles.cardDeleteMenuItemText}
                              {...webDataSet({ exerciseTypeBuilderCardMenuText: 'true' })}
                            >
                              Delete
                            </Text>
                          </TouchableOpacity>
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </>
  );

  const renderMoveTypeDestPanel = () => (
    <View style={styles.moveExercisePanel}>
      {moveSelectedTypeIds.size === 0 ? (
        <MovePickDropdown
          styles={styles}
          triggerText="Please select an exercise type to move first"
          isPlaceholder
          disabled
          open={false}
          onToggle={() => {}}
          hasArrow={false}
        />
      ) : (
        <>
          <Text
            style={styles.moveDestSectionLabel}
            {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
          >
            Courses
          </Text>
          <MovePickDropdown
            styles={styles}
            triggerText={
              selectedMoveDestCourseName
                ? `Move exercise type to another Course… ${selectedMoveDestCourseName}`
                : 'Move exercise type to another Course…'
            }
            isPlaceholder={!selectedMoveDestCourseName}
            disabled={movingTypes || courses.length === 0}
            open={showMoveDestCourseDropdown}
            onToggle={() => toggleMoveDestDropdown('course')}
            hasArrow={courses.length > 0}
          >
            <MovePickOptions
              styles={styles}
              items={courses}
              selectedId={moveDestKind === 'course' ? moveDestCourseId : null}
              currentId={currentMoveCourseId}
              currentSuffix="current course"
              getName={(c) => c.course_name}
              onSelect={handleMoveDestCourseSelect}
              emptyText="No courses available."
            />
          </MovePickDropdown>

          <Text
            style={styles.moveExerciseOrLabel}
            {...webDataSet({ exerciseTypeBuilderMoveOr: 'true' })}
          >
            Or
          </Text>
          <Text
            style={styles.moveDestSectionLabel}
            {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
          >
            Grade exercises
          </Text>
          <MovePickDropdown
            styles={styles}
            triggerText={
              selectedMoveDestGradeName
                ? `Move exercise type to a grade… ${selectedMoveDestGradeName}`
                : 'Move exercise type to a grade…'
            }
            isPlaceholder={!selectedMoveDestGradeName}
            disabled={movingTypes || grades.length === 0}
            open={showMoveDestGradeDropdown}
            onToggle={() => toggleMoveDestDropdown('grade')}
            hasArrow={grades.length > 0}
          >
            <MovePickOptions
              styles={styles}
              items={grades}
              selectedId={moveDestKind === 'grade' ? moveDestGradeId : null}
              currentId={currentMoveGradeId}
              currentSuffix="current grade"
              getName={(g) => g.grade_name}
              onSelect={handleMoveDestGradeSelect}
              emptyText="No grades available."
            />
          </MovePickDropdown>

          <Text
            style={styles.moveExerciseOrLabel}
            {...webDataSet({ exerciseTypeBuilderMoveOr: 'true' })}
          >
            Or
          </Text>
          <Text
            style={styles.moveDestSectionLabel}
            {...webDataSet({ exerciseTypeBuilderMoveDestText: 'true' })}
          >
            Non Course/Grade exercises
          </Text>
          <MovePickDropdown
            styles={styles}
            triggerText={
              moveDestKind === 'nonCourse'
                ? 'Move exercise type to Non Course/Grade… Non Course/Grade exercises'
                : 'Move exercise type to Non Course/Grade…'
            }
            isPlaceholder={moveDestKind !== 'nonCourse'}
            disabled={movingTypes}
            open={showMoveDestNonCourseDropdown}
            onToggle={() => toggleMoveDestDropdown('nonCourse')}
            hasArrow
          >
            <MovePickOptions
              styles={styles}
              items={[{ id: 'nonCourse', label: 'Non Course/Grade exercises' }]}
              selectedId={moveDestKind === 'nonCourse' ? 'nonCourse' : null}
              currentId={isCurrentlyNonCourseParent ? 'nonCourse' : null}
              currentSuffix="current"
              getName={(item) => item.label}
              onSelect={handleMoveDestNonCourseSelect}
              emptyText="No non-course/grade destination."
            />
          </MovePickDropdown>
        </>
      )}

      <View style={styles.buttonContainer}>
        <View style={styles.resetBtnShell}>
          <TouchableOpacity
            style={styles.resetBtnInner}
            onPress={handleMoveTypesCancel}
            disabled={movingTypes}
            activeOpacity={0.85}
          >
            <Text style={styles.resetBtnText}>Cancel</Text>
          </TouchableOpacity>
        </View>
        <View
          style={[styles.saveBtnShell, !canSubmitMoveTypes && styles.saveBtnDisabled]}
          {...webDataSet({ exerciseTypeBuilderSaveBtn: 'true' })}
        >
          <TouchableOpacity
            style={styles.saveBtnInner}
            onPress={() => void handleMoveTypesUpdate()}
            disabled={!canSubmitMoveTypes}
            activeOpacity={0.85}
          >
            {movingTypes ? (
              <ActivityIndicator color="#000000" size="small" />
            ) : (
              <Text style={styles.saveBtnText}>Update</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={styles.container}
      data-screen="exercise-type-builder"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View
        style={styles.header}
        {...(Platform.OS === 'web'
          ? { dataSet: { header: 'true', lightMode: !theme.isDarkMode ? 'true' : 'false' } }
          : { 'data-header': 'true', 'data-light-mode': !theme.isDarkMode })}
      >
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Exercise Type Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={styles.title}>Exercise Type Builder</Text>
          <Text style={styles.subtitle}>
            These are exercise groupings. For iron Fist, this could be Iron Fist bag conditioning,
            finger walking, handstands. The Exercise itself is Slap, eagle claw finger walking,
            knuckle handstand - which is in the Exercise Builder.
          </Text>
        </View>

        <View style={styles.pageTabRow}>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'new' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('new')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'new' && styles.pageTabTextOn]}>
              New
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('edit')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>
              Edit
            </Text>
          </TouchableOpacity>
        </View>

        {builderTab === 'new' && saveStatus && (
          <View style={styles.form}>
            <View
              style={[
                styles.statusBox,
                saveStatus === 'success' ? styles.successBox : styles.errorBox,
              ]}
            >
              <Text
                style={[
                  styles.statusBoxTitle,
                  saveStatus === 'success' && styles.successBoxText,
                  saveStatus === 'error' && styles.errorBoxText,
                ]}
              >
                {saveStatus === 'success' ? 'Record Created Successfully!' : 'Error'}
              </Text>
              {saveStatus === 'error' && errorMessage ? (
                <Text style={styles.errorMessage}>{errorMessage}</Text>
              ) : null}
              {saveStatus === 'success' ? (
                <View style={styles.statusButtons}>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.backButton]}
                    onPress={() =>
                      navigation.navigate('BuilderMain', { userId: currentUser?.id || 1 })
                    }
                  >
                    <Text style={styles.statusButtonText}>Back to Builders page</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.resetStatusButton]}
                    onPress={handleNewReset}
                  >
                    <Text style={styles.statusButtonText}>Reset form</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  style={[styles.statusButton, styles.resetStatusButton]}
                  onPress={() => {
                    setSaveStatus(null);
                    setErrorMessage('');
                  }}
                >
                  <Text style={styles.statusButtonText}>Try Again</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {builderTab === 'new' && !saveStatus ? (
          <View style={styles.form}>
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Exercise Type Name *</Text>
              <TextInput
                style={styles.input}
                value={exerciseTypeName}
                onChangeText={setExerciseTypeName}
                placeholder="Enter exercise type name"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                editable={!saving}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Equipment Needed</Text>
              <TextInput
                style={styles.input}
                value={equipmentNeeded}
                onChangeText={setEquipmentNeeded}
                placeholder="e.g., platform, Myfa(bag), steel plate"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                editable={!saving}
              />
            </View>

            <View
              style={[
                styles.fieldContainer,
                isNewCourseSectionLocked && styles.selectionSectionLocked,
              ]}
              pointerEvents={isNewCourseSectionLocked ? 'none' : 'auto'}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseTypeBuilderPathSection: 'course' } }
                : {})}
            >
              <Text style={styles.label}>Course</Text>
              <Text style={styles.helpText}>
                Optional. You can only link to courses you are enrolled in.
              </Text>
              <PickDropdown
                open={openDropdown === DROPDOWN.NEW_COURSE}
                onToggle={() => toggleDropdown(DROPDOWN.NEW_COURSE)}
                onClose={closeAllDropdowns}
                disabled={saving || creatableCourses.length === 0}
                selectedLabel={selectedNewCourseLabel}
                placeholder={
                  creatableCourses.length === 0
                    ? 'No enrolled courses available'
                    : 'Select course'
                }
                hasOptions={creatableCourses.length > 0}
                title="Course"
                styles={styles}
              >
                {creatableCourses.map((course) => (
                  <TouchableOpacity
                    key={course.id}
                    style={[
                      styles.pickerRow,
                      selectedCourseId === course.id && styles.pickerRowSelected,
                    ]}
                    onPress={() => handleNewCourseSelect(course)}
                    disabled={saving}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.dropdownOptionText,
                        selectedCourseId === course.id && styles.pickerRowTextSelected,
                      ]}
                    >
                      {course.course_name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </PickDropdown>
            </View>

            <View
              style={[
                styles.fieldContainer,
                isNewGradeSectionLocked && styles.selectionSectionLocked,
              ]}
              pointerEvents={isNewGradeSectionLocked ? 'none' : 'auto'}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseTypeBuilderPathSection: 'grade' } }
                : {})}
            >
              <Text style={styles.label}>Grade</Text>
              <Text style={styles.helpText}>
                Optional. Grades more than one rank above your own cannot be selected.
              </Text>
              <PickDropdown
                open={openDropdown === DROPDOWN.NEW_GRADE}
                onToggle={() => toggleDropdown(DROPDOWN.NEW_GRADE)}
                onClose={closeAllDropdowns}
                disabled={saving || grades.length === 0}
                selectedLabel={selectedNewGradeLabel}
                placeholder={grades.length === 0 ? 'No grades available' : 'Select grade'}
                hasOptions={grades.length > 0}
                title="Grade"
                styles={styles}
              >
                {grades.map((grade) => {
                  const gradeDisabled = isGradeBeyondViewerAccess(grade, currentUser);
                  return (
                  <TouchableOpacity
                    key={grade.id}
                    style={[
                      styles.pickerRow,
                      selectedGradeId === grade.id && styles.pickerRowSelected,
                      gradeDisabled && styles.pickerRowDisabled,
                    ]}
                    onPress={() => handleNewGradeSelect(grade)}
                    disabled={saving || gradeDisabled}
                    activeOpacity={gradeDisabled ? 1 : 0.7}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.dropdownOptionText,
                        selectedGradeId === grade.id && styles.pickerRowTextSelected,
                        gradeDisabled && styles.pickerRowTextDisabled,
                      ]}
                    >
                      {grade.grade_name}
                    </Text>
                  </TouchableOpacity>
                  );
                })}
              </PickDropdown>
            </View>

            <View
              style={[
                styles.fieldContainer,
                styles.pathSectionNonCourse,
                isNewNonCourseSectionLocked && styles.selectionSectionLocked,
              ]}
              pointerEvents={isNewNonCourseSectionLocked ? 'none' : 'auto'}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseTypeBuilderPathSection: 'nonCourse' } }
                : {})}
            >
              <Text style={styles.label}>Non course/grade exercise types</Text>
              <Text style={styles.helpText}>
                Standalone exercise types not linked to any course or grade.
              </Text>
              {!canCreateNonCourse ? (
                <Text style={styles.creationAccessHint}>
                  You must be at least 1st degree black belt to create a standalone exercise type.
                </Text>
              ) : (
                <>
                  <TouchableOpacity
                    style={[
                      styles.pickTrigger,
                      newSelectionPath === 'nonCourse' && styles.pickerRowSelected,
                    ]}
                    onPress={handleNewNonCoursePathSelect}
                    disabled={saving}
                    activeOpacity={0.7}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.pickTriggerText,
                        newSelectionPath !== 'nonCourse' && styles.pickTriggerPlaceholder,
                      ]}
                    >
                      {newSelectionPath === 'nonCourse'
                        ? 'Standalone exercise type (selected)'
                        : 'Create standalone exercise type (no course or grade)'}
                    </Text>
                  </TouchableOpacity>
                  {newSelectionPath === 'nonCourse' ? (
                    <Text style={styles.helpText}>
                      Enter the exercise type name above, then save.
                    </Text>
                  ) : null}
                  {loadingNonCourseTypes ? (
                    <ActivityIndicator
                      style={styles.exerciseOrderLoading}
                      color={theme.colors.primary || '#d4af37'}
                    />
                  ) : editNonCourseExerciseTypes.length > 0 ? (
                    <Text style={styles.helpText}>
                      {editNonCourseExerciseTypes.length} existing standalone type
                      {editNonCourseExerciseTypes.length === 1 ? '' : 's'} — use Edit to manage them.
                    </Text>
                  ) : null}
                </>
              )}
            </View>

            {renderActionButtons({ onReset: handleNewReset, onSave: handleNewSave })}
          </View>
        ) : null}

        {builderTab === 'edit' ? (
          <View style={styles.form}>
            {editSaveStatus ? (
              <View
                style={[
                  styles.editStatusBanner,
                  editSaveStatus === 'success'
                    ? styles.editStatusBannerSuccess
                    : styles.editStatusBannerError,
                ]}
                {...(Platform.OS === 'web'
                  ? {
                      dataSet: {
                        exerciseTypeBuilderEditToast: 'true',
                        exerciseTypeBuilderEditToastType: editSaveStatus,
                      },
                    }
                  : {})}
              >
                <Text
                  style={styles.editStatusBannerText}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseTypeBuilderEditToastText: 'true' } }
                    : {})}
                >
                  {editSaveStatus === 'success'
                    ? editErrorMessage || 'Changes saved successfully.'
                    : editErrorMessage || 'Could not save changes.'}
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    setEditSaveStatus(null);
                    setEditErrorMessage('');
                  }}
                  style={styles.editStatusBannerClose}
                  hitSlop={12}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss"
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseTypeBuilderEditToastClose: 'true' } }
                    : {})}
                >
                  <MaterialIcons name="close" size={20} color="#ffffff" />
                </TouchableOpacity>
              </View>
            ) : null}

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Course</Text>
              <Text style={styles.helpText}>Select a course to edit an exercise type.</Text>
              <PickDropdown
                open={openDropdown === DROPDOWN.EDIT_COURSE}
                onToggle={() => toggleDropdown(DROPDOWN.EDIT_COURSE)}
                onClose={closeAllDropdowns}
                disabled={saving || courses.length === 0}
                selectedLabel={selectedEditCourseLabel}
                placeholder={
                  courses.length === 0 ? 'No courses available' : 'Select course'
                }
                hasOptions={courses.length > 0}
                title="Course"
                styles={styles}
              >
                {courses.map((course) => (
                  <TouchableOpacity
                    key={course.id}
                    style={[
                      styles.pickerRow,
                      editCourseId === course.id && styles.pickerRowSelected,
                    ]}
                    onPress={() => handleEditCourseSelect(course)}
                    disabled={saving}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.dropdownOptionText,
                        editCourseId === course.id && styles.pickerRowTextSelected,
                      ]}
                    >
                      {course.course_name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </PickDropdown>
            </View>

            {editCourseId ? (
              <View style={styles.fieldContainer}>
                <View style={styles.exerciseListHeaderRow}>
                  <View style={styles.exerciseListHeaderTextCol}>
                    <Text style={styles.label}>Exercise types</Text>
                    <Text style={styles.helpText}>
                      {moveTypeMode
                        ? 'Select one or more exercise types, then choose a destination below.'
                        : 'Tap an exercise type to edit.'}
                    </Text>
                  </View>
                  {renderMoveTypeToggle()}
                </View>
                {renderTypePickList(
                  editExerciseTypes,
                  'course',
                  'No exercise types linked to this course.'
                )}
                {moveTypeMode ? renderMoveTypeDestPanel() : null}
              </View>
            ) : null}

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Grade exercise types</Text>
              <Text style={styles.helpText}>
                Select a grade to view and edit exercise types linked to that grade.
              </Text>
              <PickDropdown
                open={openDropdown === DROPDOWN.EDIT_GRADE}
                onToggle={() => toggleDropdown(DROPDOWN.EDIT_GRADE)}
                onClose={closeAllDropdowns}
                disabled={saving || grades.length === 0}
                selectedLabel={selectedGradeLabel}
                placeholder={grades.length === 0 ? 'No grades available' : 'Select grade'}
                hasOptions={grades.length > 0}
                title="Grade"
                styles={styles}
              >
                {grades.map((grade) => (
                  <TouchableOpacity
                    key={grade.id}
                    style={[
                      styles.pickerRow,
                      editGradeId === grade.id && styles.pickerRowSelected,
                    ]}
                    onPress={() => handleEditGradeSelect(grade)}
                    disabled={saving}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.dropdownOptionText,
                        editGradeId === grade.id && styles.pickerRowTextSelected,
                      ]}
                    >
                      {grade.grade_name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </PickDropdown>

              {editGradeId ? (
                <>
                  {loadingGradeExerciseTypes ? (
                    <ActivityIndicator
                      style={styles.exerciseOrderLoading}
                      color={theme.colors.primary || '#d4af37'}
                    />
                  ) : (
                    <View style={{ marginTop: 12 }}>
                      <View style={styles.exerciseListHeaderRow}>
                        <View style={styles.exerciseListHeaderTextCol}>
                          <Text style={styles.label}>Exercise types</Text>
                          <Text style={styles.helpText}>
                            {moveTypeMode
                              ? 'Select one or more exercise types, then choose a destination below.'
                              : 'Tap an exercise type to edit.'}
                          </Text>
                        </View>
                        {!editCourseId ? renderMoveTypeToggle() : null}
                      </View>
                      {renderTypePickList(
                        editGradeExerciseTypes,
                        'grade',
                        'No exercise types linked to this grade.'
                      )}
                      {!editCourseId && moveTypeMode ? renderMoveTypeDestPanel() : null}
                    </View>
                  )}
                </>
              ) : null}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Non course/grade exercise types</Text>
              <Text style={styles.helpText}>
                Exercise types not linked to any course or grade that you have access to.
              </Text>
              {loadingNonCourseTypes ? (
                <ActivityIndicator
                  style={styles.exerciseOrderLoading}
                  color={theme.colors.primary || '#d4af37'}
                />
              ) : !currentUser?.id ? (
                <Text style={styles.helpText}>Loading user…</Text>
              ) : (
                <>
                  <View style={styles.exerciseListHeaderRow}>
                    <View style={styles.exerciseListHeaderTextCol}>
                      <Text style={styles.helpText}>
                        {moveTypeMode && !editCourseId && !editGradeId
                          ? 'Select one or more exercise types, then choose a destination below.'
                          : 'Tap an exercise type to edit.'}
                      </Text>
                    </View>
                    {!editCourseId && !editGradeId ? renderMoveTypeToggle() : null}
                  </View>
                  {renderTypePickList(
                    editNonCourseExerciseTypes,
                    'nonCourse',
                    'No non-course/grade exercise types available.'
                  )}
                  {!editCourseId && !editGradeId && moveTypeMode
                    ? renderMoveTypeDestPanel()
                    : null}
                </>
              )}
            </View>

            {editExerciseTypeId && !moveTypeMode ? (
              <>
                <View style={styles.fieldContainer}>
                  <View style={styles.editFormHeaderRow}>
                    <Text style={[styles.label, styles.editFormHeaderLabel]}>Exercise Type Name</Text>
                    <TouchableOpacity
                      style={styles.editFormDeleteBtn}
                      onPress={() =>
                        void openTypeDeleteConfirm({
                          id: editExerciseTypeId,
                          exercise_type_name: editTypeName || 'this exercise type',
                        })
                      }
                      disabled={saving || deletingType}
                      accessibilityRole="button"
                      accessibilityLabel="Delete exercise type"
                      {...webDataSet({ exerciseTypeBuilderEditDelete: 'true' })}
                    >
                      <MaterialIcons name="delete-outline" size={20} color="#c62828" />
                      <Text style={styles.editFormDeleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                  <TextInput
                    style={styles.input}
                    value={editTypeName}
                    onChangeText={setEditTypeName}
                    placeholder="Exercise type name"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    editable={!saving}
                  />
                </View>

                <View style={styles.fieldContainer}>
                  <Text style={styles.label}>Exercises</Text>
                  <Text style={styles.helpText}>
                    Use ↑ ↓ to reorder. Use the menu on each row to move an exercise to another
                    {editSelectionSource === 'nonCourse'
                      ? ' non-course/grade type.'
                      : editSelectionSource === 'grade'
                        ? ' type in this grade.'
                        : ' type in this course.'}
                  </Text>
                  {pendingMoves.length > 0 ? (
                    <Text style={styles.pendingMovesHint}>
                      {pendingMoves.length} exercise(s) will move when you save.
                    </Text>
                  ) : null}
                  {loadingEditExercises ? (
                    <ActivityIndicator
                      style={styles.exerciseOrderLoading}
                      color={theme.colors.primary || '#d4af37'}
                    />
                  ) : editExerciseOrderList.length === 0 ? (
                    <Text style={styles.helpText}>
                      {pendingMoves.length > 0
                        ? 'All exercises in this type are staged to move. Save to apply.'
                        : 'No exercises in this type yet.'}
                    </Text>
                  ) : (
                    <ScrollView
                      style={styles.exerciseOrderList}
                      nestedScrollEnabled
                      {...(Platform.OS === 'web'
                        ? { dataSet: { exerciseTypeOrderList: 'true' } }
                        : {})}
                    >
                      {editExerciseOrderList.map((item, index) => (
                        <View
                          key={String(item.id)}
                          style={styles.exerciseOrderItem}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { exerciseTypeOrderItem: 'true' } }
                            : {})}
                        >
                          <View style={styles.exerciseOrderControls}>
                            <TouchableOpacity
                              style={[styles.orderButton, index === 0 && styles.orderButtonDisabled]}
                              onPress={() => handleMoveExerciseOrder(index, 'up')}
                              disabled={index === 0 || saving}
                            >
                              <Text style={styles.orderButtonText}>↑</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[
                                styles.orderButton,
                                index === editExerciseOrderList.length - 1 &&
                                  styles.orderButtonDisabled,
                              ]}
                              onPress={() => handleMoveExerciseOrder(index, 'down')}
                              disabled={index === editExerciseOrderList.length - 1 || saving}
                            >
                              <Text style={styles.orderButtonText}>↓</Text>
                            </TouchableOpacity>
                          </View>
                          <Text style={styles.exerciseOrderItemName} numberOfLines={2}>
                            {index + 1}. {item.exercise_name}
                          </Text>
                          <View style={styles.moveMenuWrap}>
                            <TouchableOpacity
                              style={styles.moveMenuButton}
                              onPress={() =>
                                setOpenMoveMenuExerciseId(
                                  openMoveMenuExerciseId === item.id ? null : item.id
                                )
                              }
                              disabled={saving}
                              accessibilityRole="button"
                              accessibilityLabel={`Actions for ${item.exercise_name}`}
                            >
                              <MaterialIcons
                                name="more-vert"
                                size={22}
                                color={theme.colors.text}
                              />
                            </TouchableOpacity>
                            {openMoveMenuExerciseId === item.id ? (
                              <View
                                style={styles.moveMenu}
                                {...(Platform.OS === 'web'
                                  ? { dataSet: { exerciseTypeMoveMenu: 'true' } }
                                  : {})}
                              >
                                {otherExerciseTypesForMove.length === 0 ? (
                                  <Text
                                    style={styles.moveMenuDisabled}
                                    {...(Platform.OS === 'web'
                                      ? { dataSet: { exerciseTypeMoveMenuText: 'true' } }
                                      : {})}
                                  >
                                    No other Exercise Type in this {moveTargetScopeLabel} to move
                                    Exercise to
                                  </Text>
                                ) : (
                                  otherExerciseTypesForMove.map((targetType) => (
                                    <TouchableOpacity
                                      key={targetType.id}
                                      style={styles.moveMenuItem}
                                      onPress={() => handleMoveExerciseToType(item, targetType)}
                                      {...(Platform.OS === 'web'
                                        ? { dataSet: { exerciseTypeMoveMenuItem: 'true' } }
                                        : {})}
                                    >
                                      <Text
                                        style={styles.moveMenuItemText}
                                        {...(Platform.OS === 'web'
                                          ? { dataSet: { exerciseTypeMoveMenuText: 'true' } }
                                          : {})}
                                      >
                                        Move Exercise to {targetType.exercise_type_name}
                                      </Text>
                                    </TouchableOpacity>
                                  ))
                                )}
                                <TouchableOpacity
                                  style={[styles.moveMenuItem, styles.moveMenuItemCreate]}
                                  onPress={handleCreateExerciseTypeFromEdit}
                                  {...(Platform.OS === 'web'
                                    ? { dataSet: { exerciseTypeMoveMenuItem: 'true' } }
                                    : {})}
                                >
                                  <Text
                                    style={styles.moveMenuItemTextCreate}
                                    {...(Platform.OS === 'web'
                                      ? { dataSet: { exerciseTypeMoveMenuText: 'true' } }
                                      : {})}
                                  >
                                    Create Exercise Type
                                  </Text>
                                </TouchableOpacity>
                              </View>
                            ) : null}
                          </View>
                        </View>
                      ))}
                    </ScrollView>
                  )}
                </View>

                {renderActionButtons({ onReset: handleEditReset, onSave: handleEditSave })}
              </>
            ) : null}
          </View>
        ) : null}
      </ScrollView>
      <BrandedConfirmModal
        visible={!!typeDeleteTarget}
        title="Delete exercise type?"
        message={
          typeDeleteTarget
            ? typeDeleteTarget.exerciseCount > 0
              ? `This will permanently delete the exercise type “${typeDeleteTarget.name}” and all ${typeDeleteTarget.exerciseCount} exercise${typeDeleteTarget.exerciseCount === 1 ? '' : 's'} in it. This cannot be undone.`
              : `This will permanently delete the exercise type “${typeDeleteTarget.name}”. This cannot be undone.`
            : ''
        }
        confirmLabel="OK"
        cancelLabel="Cancel"
        destructive
        typedConfirmWord="delete"
        typedValue={typeDeleteTypedValue}
        onTypedValueChange={setTypeDeleteTypedValue}
        typedPlaceholder="Type delete to confirm"
        busy={deletingType}
        errorMessage={typeDeleteError}
        onConfirm={() => void handleDeleteTypeConfirm()}
        onCancel={dismissTypeDeleteConfirm}
      />
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    titleSection: {
      marginBottom: 30,
      alignItems: 'center',
    },
    pageTabRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 24,
      alignSelf: 'stretch',
    },
    pageTab: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    pageTabOn: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    pageTabText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
    },
    pageTabTextOn: {
      color: '#000',
    },
    title: {
      fontSize: 36,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 10,
      textAlign: 'center',
      letterSpacing: 1,
      ...(Platform.OS === 'web'
        ? {
            fontFamily: 'serif, Georgia, "Times New Roman", Times, serif',
            textTransform: 'uppercase',
          }
        : {
            textTransform: 'uppercase',
          }),
    },
    subtitle: {
      fontSize: 14,
      fontStyle: 'italic',
      color: theme.colors.text,
      textAlign: 'center',
      paddingHorizontal: 20,
      lineHeight: 20,
      opacity: 0.8,
    },
    form: {
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
      overflow: 'visible',
    },
    fieldContainer: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginBottom: 8,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    creationAccessHint: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.75,
      marginBottom: 16,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    selectionSectionLocked: {
      opacity: 0.42,
    },
    pathSectionNonCourse: {
      marginTop: 4,
    },
    pendingMovesHint: {
      fontSize: 13,
      color: theme.colors.primary || '#d4af37',
      marginBottom: 8,
      fontWeight: '600',
    },
    input: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    dropdownContainer: {
      marginTop: 8,
    },
    pickDropdownWrap: {
      marginTop: 8,
    },
    pickTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 12,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    pickTriggerDisabled: {
      opacity: 0.7,
    },
    pickerModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    pickerModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    dropdownSheet: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: theme.isDarkMode ? '#2c2c2c' : '#FFFFFF',
      borderRadius: 12,
      padding: 16,
      maxHeight: '80%',
      zIndex: 2,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        : { elevation: 8 }),
    },
    dropdownSheetTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 10,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownScroll: {
      maxHeight: 360,
      backgroundColor: 'transparent',
    },
    dropdownScrollContent: {
      paddingBottom: 8,
      backgroundColor: 'transparent',
    },
    pickerRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      marginBottom: 6,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#3d3d3d' : '#f2f2f2',
    },
    pickerRowSelected: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    pickerRowDisabled: {
      opacity: 0.45,
    },
    pickerRowTextSelected: {
      color: '#FFFFFF',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    pickerRowTextDisabled: {
      color: theme.isDarkMode ? '#888' : '#999',
      backgroundColor: 'transparent',
    },
    pickTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    pickTriggerPlaceholder: {
      opacity: 0.65,
    },
    pickTriggerArrow: {
      fontSize: 14,
      color: theme.colors.text,
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    pickDropdownList: {
      maxHeight: 280,
      marginTop: 4,
    },
    dropdownOption: {
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    dropdownOptionSelected: {
      backgroundColor: theme.colors.primary || '#d4af37',
      borderColor: theme.colors.primary || '#d4af37',
    },
    dropdownOptionDisabled: {
      opacity: 0.45,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#F5F5F5',
    },
    dropdownOptionText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownOptionTextSelected: {
      color: '#FFFFFF',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    dropdownOptionTextDisabled: {
      color: theme.isDarkMode ? '#888' : '#999',
      backgroundColor: 'transparent',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 30,
      gap: 15,
    },
    resetBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: '#999999',
    },
    resetBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    resetBtnText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    saveBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    saveBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    saveBtnText: {
      color: '#000000',
      fontSize: 16,
      fontWeight: '700',
    },
    exerciseOrderLoading: {
      marginVertical: 12,
    },
    exerciseOrderList: {
      maxHeight: 320,
      marginTop: 8,
    },
    exerciseOrderItem: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 12,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#FDFD0A' : theme.colors.primary || '#0070D2',
    },
    exerciseOrderControls: {
      flexDirection: 'row',
      marginRight: 12,
      backgroundColor: 'transparent',
    },
    orderButton: {
      width: 32,
      height: 32,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      borderRadius: 4,
      marginRight: 4,
    },
    orderButtonDisabled: {
      opacity: 0.3,
    },
    orderButtonText: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: 'bold',
      backgroundColor: 'transparent',
    },
    exerciseOrderItemName: {
      flex: 1,
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    moveMenuWrap: {
      position: 'relative',
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    moveMenuButton: {
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
    },
    moveMenu: {
      position: 'absolute',
      top: 38,
      right: 0,
      minWidth: 260,
      maxWidth: 300,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      zIndex: 20,
      paddingVertical: 6,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 6,
            elevation: 6,
          }),
    },
    moveMenuItem: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: 'transparent',
    },
    moveMenuItemCreate: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      marginTop: 4,
    },
    moveMenuItemText: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    moveMenuItemTextCreate: {
      fontSize: 14,
      color: theme.colors.primary || '#d4af37',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    moveMenuDisabled: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.65,
      fontStyle: 'italic',
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: 'transparent',
    },
    editStatusBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 16,
    },
    editStatusBannerSuccess: {
      backgroundColor: '#2e7d32',
    },
    editStatusBannerError: {
      backgroundColor: '#c62828',
    },
    editStatusBannerText: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      fontWeight: '700',
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    editStatusBannerClose: {
      padding: 2,
      backgroundColor: 'transparent',
    },
    statusBox: {
      width: '100%',
      padding: 30,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }),
    },
    successBox: {
      backgroundColor: '#d4af37',
    },
    errorBox: {
      backgroundColor: '#DC3545',
    },
    statusBoxTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 10,
      textAlign: 'center',
    },
    successBoxText: {
      color: '#000000',
    },
    errorBoxText: {
      color: '#FFFFFF',
    },
    errorMessage: {
      fontSize: 16,
      color: '#FFFFFF',
      textAlign: 'center',
      marginBottom: 20,
      paddingHorizontal: 10,
    },
    statusButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: 20,
      gap: 15,
    },
    statusButton: {
      flex: 1,
      padding: 15,
      borderRadius: 8,
      alignItems: 'center',
    },
    backButton: {
      backgroundColor: '#000000',
    },
    resetStatusButton: {
      backgroundColor: '#333333',
    },
    statusButtonText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    exerciseListHeaderRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 8,
    },
    exerciseListHeaderTextCol: {
      flex: 1,
      maxWidth: '50%',
      minWidth: 0,
    },
    moveTypeToggleWrap: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      flexShrink: 0,
      maxWidth: '50%',
      gap: 8,
      paddingTop: 2,
    },
    moveTypeToggleLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
      textAlign: 'right',
    },
    segmentedToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 24,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#555555' : '#CCCCCC',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#E8E8E8',
      padding: 3,
      minHeight: 44,
      minWidth: 132,
    },
    segmentedToggleOption: {
      flex: 1,
      minWidth: 60,
      minHeight: 38,
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    segmentedToggleOptionActiveOff: {
      backgroundColor: theme.isDarkMode ? '#757575' : '#9E9E9E',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.2,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    segmentedToggleOptionActiveOn: {
      backgroundColor: '#4CAF50',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.25,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    segmentedToggleText: {
      fontSize: 13,
      fontWeight: '700',
      letterSpacing: 0.4,
      color: theme.isDarkMode ? '#CCCCCC' : '#666666',
      backgroundColor: 'transparent',
    },
    segmentedToggleTextActive: {
      color: '#FFFFFF',
    },
    moveExercisePanel: {
      marginTop: 8,
      marginBottom: 20,
    },
    moveTypeDropdownList: {
      maxHeight: 240,
      marginTop: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
      overflow: 'hidden',
    },
    moveDestDropdownWrap: {
      marginTop: 0,
    },
    moveDestOptionsScroll: {
      maxHeight: 220,
    },
    moveDestEmptyText: {
      padding: 12,
      backgroundColor: 'transparent',
    },
    moveExerciseOrLabel: {
      marginTop: 16,
      marginBottom: 8,
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
      textAlign: 'center',
    },
    moveDestSectionLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
      marginBottom: 8,
      marginTop: 4,
    },
    typePickList: {
      marginTop: 8,
    },
    typePickCard: {
      padding: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      marginBottom: 10,
      minHeight: 56,
      position: 'relative',
      overflow: 'hidden',
    },
    typePickCardMenuOpen: {
      overflow: 'visible',
      zIndex: 8,
    },
    typePickCardInner: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    typePickCardMain: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    typePickCardSelected: {
      borderColor: '#d4af37',
    },
    cardMenuWrap: {
      position: 'relative',
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    cardMenuButton: {
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      backgroundColor: 'transparent',
    },
    cardDeleteMenu: {
      position: 'absolute',
      top: 38,
      right: 0,
      minWidth: 140,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      zIndex: 20,
      paddingVertical: 4,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 6,
            elevation: 6,
          }),
    },
    cardDeleteMenuItem: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: 'transparent',
    },
    cardDeleteMenuItemText: {
      fontSize: 15,
      fontWeight: '600',
      color: '#c62828',
      backgroundColor: 'transparent',
    },
    editFormHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 8,
    },
    editFormHeaderLabel: {
      flex: 1,
      marginBottom: 0,
    },
    editFormDeleteBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 4,
      paddingHorizontal: 4,
      backgroundColor: 'transparent',
    },
    editFormDeleteBtnText: {
      fontSize: 14,
      fontWeight: '700',
      color: '#c62828',
      backgroundColor: 'transparent',
    },
    typePickCardContent: {
      position: 'relative',
      zIndex: 1,
      backgroundColor: 'transparent',
    },
    typePickYellowOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#d4af37',
      opacity: 0.5,
      borderRadius: 10,
    },
    typePickNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    typePickEditIcon: {
      marginRight: 8,
    },
    typePickName: {
      flex: 1,
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      backgroundColor: 'transparent',
    },
    typePickNameSelected: {
      color: '#FFFFFF',
    },
    saveBtnDisabled: {
      opacity: 0.45,
    },
  });
